import { ClientMethodsTypes, client } from '@/services/v2/client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useUser } from 'containers/user/hooks/useUser'
import { useProfile } from 'containers/profile/hooks/useProfile'
import { ProfileQueryKeys } from 'containers/profile/types/query-keys'
import { Highlight } from '../types/highlights'
import { useEffect, useMemo, useState } from 'react'
import { useHighlightEditorStore } from '../store/highlightEditor'
import { validateHighlightErrorByLabel } from '../misc/validateHighlightData'

const BASE_URL = '/api/v1/users/highlight'
// Get highlights
export const useHighlights = () => {
  const { profile } = useProfile()

  const { data, isLoading, error } = useQuery({
    queryKey: [ProfileQueryKeys.HIGHLIGHTS, profile?.id],
    queryFn: () =>
      client<Highlight[]>(`${BASE_URL}/get-by-user/${profile?.id}`),
    enabled: !!profile?.id,
    retry: false,
  })

  const highlights = useMemo(() => {
    // Sort by createdOn desc
    return (
      data?.sort((a, b) => {
        const dateA = new Date(a.createdOn)
        const dateB = new Date(b.createdOn)
        return dateB.getTime() - dateA.getTime()
      }) || []
    )
  }, [data])

  return {
    highlights: highlights,
    isLoading,
    error,
  }
}

export const useHighlight = (highlightId: string) => {
  const { data: highlight, ...rest } = useQuery({
    queryKey: [ProfileQueryKeys.HIGHLIGHTS, highlightId],
    queryFn: () => client<Highlight>(`${BASE_URL}/${highlightId}`),
    enabled: !!highlightId,
  })

  return {
    highlight,
    ...rest,
  }
}

// Create and update highlights

export const useCreateHighlight = () => {
  const { user } = useUser()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (highlight: Partial<Highlight>) =>
      client<Highlight>(`${BASE_URL}/create`, {
        method: ClientMethodsTypes.POST,
        body: {
          ...highlight,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([ProfileQueryKeys.HIGHLIGHTS, user?.id])
    },
  })
}

export const useUpdateHighlight = () => {
  const { user } = useUser()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (highlight: Partial<Highlight>) =>
      client<Highlight>(`${BASE_URL}/${highlight.id}`, {
        method: ClientMethodsTypes.PATCH,
        body: highlight,
      }),
    onSuccess: (_, highlight) => {
      queryClient.invalidateQueries([
        ProfileQueryKeys.HIGHLIGHTS,
        highlight?.id,
      ])
      queryClient.invalidateQueries([ProfileQueryKeys.HIGHLIGHTS, user?.id])
    },
  })
}

// Delete highlights
export const useDeleteHighlight = () => {
  const { user } = useUser()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (highlightId: string) =>
      client<Highlight>(`${BASE_URL}/${highlightId}`, {
        method: ClientMethodsTypes.DELETE,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([ProfileQueryKeys.HIGHLIGHTS, user?.id])
    },
  })
}

// Error handler
export const useHighlightError = (label: keyof Highlight) => {
  const [error, setError] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const { errors } = useHighlightEditorStore()

  useEffect(() => {
    const errorByLabel = validateHighlightErrorByLabel(label, errors)
    if (errorByLabel) {
      setError(true)
      setMessage(errorByLabel)
    } else {
      setError(false)
      setMessage('')
    }
  }, [errors, label])

  return {
    error,
    errorMessage: message,
  }
}
